import React, { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useAppVersion } from '../hooks/useAppVersion';
import { RefreshCcw, DownloadCloud, AlertTriangle } from 'lucide-react';

export const ReloadPrompt: React.FC = () => {
    // 1. Hook Workbox Service Worker PWA (Precache layer)
    const {
        offlineReady: [offlineReady, setOfflineReady],
        needRefresh: [needRefresh, setNeedRefresh],
    } = useRegisterSW({
        onRegistered(r) {
            if (r) {
                setInterval(() => {
                    r.update();
                }, 60 * 60 * 1000); // Check SW update every hour
            }
        },
        onRegisterError(error) {
            console.warn('SW registration error:', error);
        },
    });

    // 2. Hook App Version terpusat database Supabase (Release Versioning layer)
    const {
        currentVersion,
        latestVersionData,
        isUpdateAvailable,
        isForceUpdate,
        loading,
        handleForceUpdateApp
    } = useAppVersion({ poll: true });

    // SATU modal update saja. Modal baru boleh tampil setelah pengecekan
    // versi PERTAMA selesai, supaya jenisnya (hijau "Update Sistem Tersedia"
    // atau merah "Pembaruan Wajib!") sudah pasti sejak awal. Dulu modal bisa
    // muncul hijau lebih dulu (dipicu service worker), lalu berganti merah
    // setelah data versi selesai dimuat — terlihat seperti beberapa modal.
    // Pengecekan berkala berikutnya tidak menyembunyikan modal lagi.
    const [versionChecked, setVersionChecked] = useState<boolean>(false);
    useEffect(() => {
        if (!loading) setVersionChecked(true);
    }, [loading]);

    // Notifikasi "Aplikasi Siap (offline)" dulu tampil sebagai modal putih
    // di atas layar gelap. Sekarang tidak ditampilkan lagi supaya satu-satunya
    // modal yang berkaitan dengan pembaruan adalah modal update di bawah.
    useEffect(() => {
        if (offlineReady) setOfflineReady(false);
    }, [offlineReady, setOfflineReady]);

    // Dismiss diingat PER VERSI: modal hanya disembunyikan untuk versi yang
    // sama persis dengan yang di-dismiss. Begitu admin merilis versi baru di
    // Supabase, modal wajib muncul lagi untuk versi itu (dan hanya sekali).
    const [dismissedVersion, setDismissedVersion] = useState<string>(() => {
        try {
            return sessionStorage.getItem('app_update_modal_dismissed') || '';
        } catch {
            return '';
        }
    });
    const [isUpdating, setIsUpdating] = useState<boolean>(false);

    const isDismissed = dismissedVersion !== '' && dismissedVersion === (latestVersionData?.version || '');

    // Penentuan apakah modal update harus ditampilkan:
    // Tampilkan jika Service Worker membutuhkan refresh (needRefresh) ATAU ada
    // rilis versi baru di database (isUpdateAvailable) ATAU proses update
    // sedang berjalan (isUpdating — indikator "Memuat Ulang..." ada DI DALAM
    // modal ini, jadi modal harus tetap tampil selama proses berlangsung).
    const shouldShowUpdateModal = isUpdating || (
        versionChecked
        && (needRefresh || isUpdateAvailable)
        && (isForceUpdate || !isDismissed)
    );

    const handleClose = () => {
        if (isForceUpdate) return; // Tidak bisa ditutup jika pembaruan wajib
        setNeedRefresh(false);
        const versionToDismiss = latestVersionData?.version || '';
        try {
            sessionStorage.setItem('app_update_modal_dismissed', versionToDismiss);
        } catch {
            // Abaikan kegagalan storage
        }
        setDismissedVersion(versionToDismiss);
    };

    const handleExecuteUpdate = async () => {
        if (isUpdating) return; // Cegah klik ganda sebelum async berjalan
        setIsUpdating(true);
        try {
            // handleForceUpdateApp (jalur update cepat) menunggu service worker
            // BARU selesai mengunduh file yang berubah, aktif & mengontrol
            // halaman sebelum reload — sehingga cukup SEKALI klik, reload PASTI
            // menyajikan bundle baru (versi benar-benar naik).
            await handleForceUpdateApp();
        } catch (err) {
            console.error('Error executing update:', err);
            setIsUpdating(false);
            window.location.reload();
        }
    };

    if (!shouldShowUpdateModal) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
            {/* Satu-satunya modal update: hijau (opsional) atau merah (wajib) */}
            {(
                <div className="bg-white border border-gray-100 shadow-2xl rounded-3xl overflow-hidden max-w-sm w-full animate-in zoom-in-95 duration-300">

                    {/* Header Banner Modal */}
                    <div className={`p-6 text-center text-white ${
                        isForceUpdate
                            ? 'bg-gradient-to-br from-rose-600 to-red-700'
                            : 'bg-gradient-to-br from-emerald-600 to-teal-700'
                    }`}>
                        <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center mx-auto mb-3 shadow-inner">
                            {isForceUpdate ? (
                                <AlertTriangle size={32} className="text-white animate-bounce" />
                            ) : (
                                <DownloadCloud size={32} className="text-white animate-pulse" />
                            )}
                        </div>
                        <h4 className="font-extrabold text-[#111813] text-xl tracking-tight text-white">
                            {isForceUpdate ? 'Pembaruan Wajib!' : 'Update Sistem Tersedia!'}
                        </h4>
                        <p className="text-xs text-white/80 mt-1">
                            {latestVersionData?.version ? `Versi v${latestVersionData.version} telah dirilis` : 'Fitur & perbaikan baru telah siap'}
                        </p>
                    </div>

                    {/* Content Section */}
                    <div className="p-6 space-y-4">
                        <div className="bg-gray-50 p-3.5 rounded-2xl border border-gray-100 flex items-center justify-between text-xs">
                            <div>
                                <span className="text-gray-400 block text-[10px] uppercase font-bold">Versi Anda</span>
                                <span className="font-mono font-bold text-gray-700 text-sm">v{currentVersion}</span>
                            </div>
                            <div className="text-right">
                                <span className="text-gray-400 block text-[10px] uppercase font-bold">Versi Terbaru</span>
                                <span className="font-mono font-bold text-emerald-600 text-sm">
                                    v{latestVersionData?.version || currentVersion}
                                </span>
                            </div>
                        </div>

                        <p className="text-xs text-gray-600 leading-relaxed text-center">
                            {isForceUpdate ? (
                                <span className="font-semibold text-rose-600">
                                    Versi aplikasi Anda tertinggal cukup jauh. Demi kelancaran dan keamanan data, Anda wajib memperbarui sistem sekarang.
                                </span>
                            ) : (
                                'Silakan klik tombol di bawah untuk menyegarkan browser dan langsung menikmati performa serta fitur terbaru.'
                            )}
                        </p>

                        {/* Rincian Perubahan (Jika Ada) */}
                        {latestVersionData?.release_notes && latestVersionData.release_notes.length > 0 && (
                            <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Catatan Pembaruan:</p>
                                <ul className="list-disc list-inside text-[11px] text-gray-700 space-y-0.5">
                                    {latestVersionData.release_notes.slice(0, 3).map((note, idx) => (
                                        <li key={idx} className="truncate">{note}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="w-full flex gap-3 pt-2">
                            {!isForceUpdate && (
                                <button
                                    onClick={handleClose}
                                    disabled={isUpdating}
                                    className="flex-1 py-3 bg-gray-100 text-gray-600 text-xs font-bold rounded-xl hover:bg-gray-200 transition-colors"
                                >
                                    Nanti
                                </button>
                            )}
                            <button
                                onClick={handleExecuteUpdate}
                                disabled={isUpdating}
                                className={`flex-1 py-3 text-white text-xs font-bold rounded-xl transition-all flex justify-center items-center gap-2 shadow-lg ${
                                    isForceUpdate
                                        ? 'bg-rose-600 hover:bg-rose-700 shadow-rose-200'
                                        : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200'
                                }`}
                            >
                                <RefreshCcw size={16} className={isUpdating ? 'animate-spin' : ''} />
                                <span>{isUpdating ? 'Mengunduh pembaruan...' : 'Perbarui Sekarang'}</span>
                            </button>
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
};
